import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { isD2RAdminEmail } from '../constants/d2rAdminEmails';
import ProjectRow, { PROJECT_COLUMN_COUNT } from './admin/ProjectRow';
import PdfDownloadButton from './admin/PdfDownloadButton';
import { downloadProposalPdf } from './admin/adminPdf';
import {
  EMAIL_TYPE_LABELS,
  OUTCOME_FILTERS,
  flattenProposals,
  formatDateTime,
  isOnOrAfterToday,
  normalizeAuthToken,
  normalizeStatus,
  projectMatchesQuery,
  statusChipSx,
  uniqueCount,
} from './admin/adminFormatting';
import { ClampedText } from './admin/RecordDetails';

const endpointpath = process.env.REACT_APP_API_BASE_URL;
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

// Each counter is defined here rather than in the JSX so the wording stays in one place
// and the cards read as a list. These are the tooltip bodies.
const KPI_HELP = {
  projects:
    'Every project on record, including employer drafts that were never published. Only Submitted projects are visible to students.',
  open:
    'Published projects whose proposal deadline has not passed. Students can still apply to these; the rest remain listed but are closed in practice.',
  proposals:
    'Every proposal on record, including saved drafts and proposals whose project has since been deleted. The sub-count is those with a resume or supporting document attached.',
  applicants:
    'Distinct students with at least one proposal, matched on email address. Lower than the proposal count when a student applies to more than one project.',
  decisions:
    'Proposals an employer has decided on, shown as accepted / rejected. Anything still awaiting a decision is counted under In Review instead.',
  inReview:
    'Proposals a student submitted that no employer has accepted or rejected yet. Withdrawn counts proposals a student later pulled.',
  drafts:
    'Proposals a student saved but never submitted. Employers can already see these in the app, so they also appear in digest emails.',
  emails:
    'Every email the platform has attempted to send — proposal decisions and both digest types — as recorded in the EmailLog table. A failed row keeps the error returned by SES.',
};

const StatCard = ({ label, value, helper, description }) => (
  <Tooltip title={description || ''} arrow placement="top" enterDelay={200}>
    <Paper
      elevation={0}
      tabIndex={0}
      sx={{
        height: '100%',
        borderRadius: 2,
        border: '1px solid rgba(80, 0, 0, 0.14)',
        background:
          'linear-gradient(135deg, rgba(233,228,220,0.95) 0%, rgba(255,255,255,1) 70%)',
        p: 2,
        cursor: description ? 'help' : 'default',
        '&:hover': description ? { borderColor: 'rgba(80, 0, 0, 0.45)' } : undefined,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography
          sx={{
            fontWeight: 900,
            color: '#500000',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            flexGrow: 1,
          }}
        >
          {label}
        </Typography>
        {description ? (
          <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'rgba(80, 0, 0, 0.45)' }} />
        ) : null}
      </Box>
      <Typography sx={{ fontSize: '2.05rem', fontWeight: 900, color: '#1f1f1f', lineHeight: 1.1, mt: 0.5 }}>
        {value}
      </Typography>
      {helper ? (
        <Typography sx={{ mt: 0.75, color: '#535353', fontWeight: 600, fontSize: '0.95rem' }}>
          {helper}
        </Typography>
      ) : null}
    </Paper>
  </Tooltip>
);

const SectionHeader = ({ title, description, children }) => (
  <>
    <Box
      sx={{
        p: 2,
        bgcolor: '#ffffff',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      <Box sx={{ flexGrow: 1, minWidth: 240 }}>
        <Typography variant="h6" sx={{ fontWeight: 900, color: '#500000' }}>
          {title}
        </Typography>
        <Typography sx={{ color: '#535353', fontWeight: 600 }}>{description}</Typography>
      </Box>
      {children}
    </Box>
    <Divider />
  </>
);

const HeadCell = ({ children, ...rest }) => (
  <TableCell sx={{ fontWeight: 900, bgcolor: '#e9e4dc' }} {...rest}>
    {children}
  </TableCell>
);

const getUserEmail = (userData) => {
  const email = userData?.Email || userData?.email || userData?.preferred_username || '';
  return (email || '').toString().trim().toLowerCase();
};

const AdminProjectProposalSummary = ({ userData }) => {
  const navigate = useNavigate();
  const userEmail = useMemo(() => getUserEmail(userData), [userData]);
  const isAdmin = isD2RAdminEmail(userEmail);

  const [projects, setProjects] = useState([]);
  const [orphanProposals, setOrphanProposals] = useState([]);
  const [fieldSchema, setFieldSchema] = useState({ project: [], proposal: [] });
  const [totals, setTotals] = useState({ totalProjects: 0, totalProposals: 0 });
  const [emails, setEmails] = useState([]);
  const [emailTotals, setEmailTotals] = useState({ total: 0, sent: 0, failed: 0 });
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [outcomeFilter, setOutcomeFilter] = useState('All');
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/unauthorized');
      return;
    }

    const fetchSummary = async () => {
      try {
        setLoading(true);
        setError('');
        const token = normalizeAuthToken(localStorage.getItem('token'));
        if (!token) {
          setError('Session expired. Please log in again.');
          setLoading(false);
          return;
        }

        const response = await fetch(`${endpointpath}/api/admin/project-proposals-summary`, {
          method: 'GET',
          headers: {
            Authorization: token,
          },
        });

        if (response.status === 403) {
          navigate('/unauthorized');
          return;
        }
        if (!response.ok) {
          throw new Error(`Request failed (${response.status})`);
        }

        const data = await response.json();
        setProjects(Array.isArray(data?.projects) ? data.projects : []);
        setOrphanProposals(Array.isArray(data?.orphanProposals) ? data.orphanProposals : []);
        setFieldSchema({
          project: Array.isArray(data?.fieldSchema?.project) ? data.fieldSchema.project : [],
          proposal: Array.isArray(data?.fieldSchema?.proposal) ? data.fieldSchema.proposal : [],
        });
        setTotals({
          totalProjects: Number(data?.totalProjects) || 0,
          totalProposals: Number(data?.totalProposals) || 0,
        });
        setLoading(false);
      } catch (e) {
        setError('Failed to load admin proposal summary.');
        setLoading(false);
      }
    };

    // Kept separate from the summary fetch so an email-log failure cannot blank the dashboard.
    const fetchEmailLog = async () => {
      try {
        setEmailError('');
        const token = normalizeAuthToken(localStorage.getItem('token'));
        if (!token) return;

        const response = await fetch(`${endpointpath}/api/admin/email-log`, {
          method: 'GET',
          headers: {
            Authorization: token,
          },
        });

        if (!response.ok) {
          throw new Error(`Request failed (${response.status})`);
        }

        const data = await response.json();
        setEmails(Array.isArray(data?.emails) ? data.emails : []);
        setEmailTotals({
          total: Number(data?.totals?.total) || 0,
          sent: Number(data?.totals?.sent) || 0,
          failed: Number(data?.totals?.failed) || 0,
        });
      } catch (e) {
        setEmailError('Failed to load the email log.');
      }
    };

    fetchSummary();
    fetchEmailLog();
  }, [isAdmin, navigate]);

  const allProposals = useMemo(
    () => flattenProposals(projects, orphanProposals),
    [projects, orphanProposals]
  );

  const projectStatuses = useMemo(() => {
    const seen = new Set(
      projects.map((p) => normalizeStatus(p.projectStatus)).filter(Boolean)
    );
    return [...seen].sort();
  }, [projects]);

  const visibleProjects = useMemo(() => {
    const byOutcome = (proposals) =>
      outcomeFilter === 'All'
        ? proposals
        : proposals.filter((p) => normalizeStatus(p.outcome) === outcomeFilter);

    return projects
      .filter((project) => statusFilter === 'All' || normalizeStatus(project.projectStatus) === statusFilter)
      .map((project) => ({ ...project, proposals: byOutcome(project.proposals || []) }))
      .filter((project) => outcomeFilter === 'All' || project.proposals.length > 0)
      .filter((project) => projectMatchesQuery(project, query));
  }, [projects, statusFilter, outcomeFilter, query]);

  useEffect(() => {
    setPage(0);
  }, [query, statusFilter, outcomeFilter, rowsPerPage]);

  const pagedProjects = useMemo(
    () => visibleProjects.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [visibleProjects, page, rowsPerPage]
  );

  const toggleProject = useCallback((projectID) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(projectID)) next.delete(projectID);
      else next.add(projectID);
      return next;
    });
  }, []);

  const allOnPageExpanded =
    pagedProjects.length > 0 && pagedProjects.every((p) => expandedIds.has(p.projectID));

  const toggleAllOnPage = () => {
    setExpandedIds((current) => {
      const next = new Set(current);
      pagedProjects.forEach((p) => {
        if (allOnPageExpanded) next.delete(p.projectID);
        else next.add(p.projectID);
      });
      return next;
    });
  };

  const kpis = useMemo(() => {
    const projectStatusCounts = projects.reduce((acc, p) => {
      const key = normalizeStatus(p?.projectStatus) || 'Unknown';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const outcomeCounts = allProposals.reduce((acc, p) => {
      const key = normalizeStatus(p?.outcome) || 'In Review';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const withFiles = allProposals.filter((p) => (p.attachments || []).length > 0).length;

    // Students only ever see published projects, so "open" is judged against those.
    const published = projects.filter((p) => normalizeStatus(p.projectStatus) === 'Submitted');
    const openProjects = published.filter((p) => isOnOrAfterToday(p.proposalDeadline));

    const drafts = allProposals.filter((p) =>
      normalizeStatus(p.outcome).toLowerCase().startsWith('saved')
    );

    return {
      totalProjects: totals.totalProjects || projects.length,
      submittedProjects: projectStatusCounts.Submitted || 0,
      closedProjects: projectStatusCounts.Closed || 0,
      openProjects: openProjects.length,
      pastDeadline: published.length - openProjects.length,
      totalProposals: totals.totalProposals || allProposals.length,
      withFiles,
      applicants: uniqueCount(allProposals.map((p) => p.applicantEmail)),
      accepted: outcomeCounts.Accepted || 0,
      rejected: outcomeCounts.Rejected || 0,
      inReview: outcomeCounts['In Review'] || 0,
      withdrawn: outcomeCounts.Withdrawn || 0,
      drafts: drafts.length,
      draftApplicants: uniqueCount(drafts.map((p) => p.applicantEmail)),
    };
  }, [projects, allProposals, totals]);

  return (
    <Box sx={{ padding: { xs: 2, md: 4 } }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Admin: Projects &amp; Proposals
      </Typography>
      <Typography variant="body1" sx={{ color: '#535353', mb: 2 }}>
        Every project on record with the student proposals it received. Expand a row for the full
        form response and uploaded files, or download any record as a PDF.
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
          <CircularProgress size={28} />
          <Typography>Loading…</Typography>
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                label="Total Projects"
                value={kpis.totalProjects}
                helper={`${kpis.submittedProjects} submitted • ${kpis.closedProjects} closed`}
                description={KPI_HELP.projects}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                label="Open for Applications"
                value={kpis.openProjects}
                helper={`${kpis.pastDeadline} past deadline`}
                description={KPI_HELP.open}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                label="Total Proposals"
                value={kpis.totalProposals}
                helper={`${kpis.withFiles} with uploaded files`}
                description={KPI_HELP.proposals}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                label="Applicants"
                value={kpis.applicants}
                helper={
                  kpis.applicants
                    ? `${(kpis.totalProposals / kpis.applicants).toFixed(1)} proposals each`
                    : 'No applicants yet'
                }
                description={KPI_HELP.applicants}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                label="Decisions"
                value={`${kpis.accepted} / ${kpis.rejected}`}
                helper="Accepted / rejected"
                description={KPI_HELP.decisions}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                label="In Review"
                value={kpis.inReview}
                helper={`${kpis.withdrawn} withdrawn`}
                description={KPI_HELP.inReview}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                label="Drafts Not Submitted"
                value={kpis.drafts}
                helper={`${kpis.draftApplicants} student${kpis.draftApplicants === 1 ? '' : 's'}`}
                description={KPI_HELP.drafts}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                label="Emails Logged"
                value={emailTotals.total}
                helper={`${emailTotals.sent} sent • ${emailTotals.failed} failed`}
                description={KPI_HELP.emails}
              />
            </Grid>
          </Grid>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: '1px solid rgba(0,0,0,0.08)',
              overflow: 'hidden',
              mb: 3,
            }}
          >
            <SectionHeader
              title="Projects and their proposals"
              description="One row per project. The proposals column links to each student's full record."
            >
              <TextField
                size="small"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search projects, students, UIN…"
                sx={{ minWidth: 260 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: '1.15rem', color: '#6b6b6b' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                size="small"
                select
                label="Project status"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="All">All</MenuItem>
                {projectStatuses.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                select
                label="Proposal outcome"
                value={outcomeFilter}
                onChange={(event) => setOutcomeFilter(event.target.value)}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="All">All</MenuItem>
                {OUTCOME_FILTERS.map((outcome) => (
                  <MenuItem key={outcome} value={outcome}>
                    {outcome}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                size="small"
                onClick={toggleAllOnPage}
                disabled={pagedProjects.length === 0}
                sx={{ fontWeight: 800, color: '#500000' }}
              >
                {allOnPageExpanded ? 'Collapse all' : 'Expand all'}
              </Button>
            </SectionHeader>

            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <HeadCell />
                    <HeadCell>Project</HeadCell>
                    <HeadCell>Requester</HeadCell>
                    <HeadCell>Dates</HeadCell>
                    <HeadCell>Status</HeadCell>
                    <HeadCell>Funding</HeadCell>
                    <HeadCell>Student proposals (PDF)</HeadCell>
                    <HeadCell>Record</HeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedProjects.map((project) => (
                    <ProjectRow
                      key={project.projectID}
                      project={project}
                      fieldSchema={fieldSchema}
                      expanded={expandedIds.has(project.projectID)}
                      onToggle={() => toggleProject(project.projectID)}
                    />
                  ))}
                  {visibleProjects.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={PROJECT_COLUMN_COUNT}>
                        <Typography sx={{ color: '#707070', fontWeight: 800 }}>
                          {projects.length === 0
                            ? 'No projects found.'
                            : 'No projects match the current search and filters.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={visibleProjects.length}
              page={page}
              onPageChange={(event, nextPage) => setPage(nextPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => setRowsPerPage(parseInt(event.target.value, 10))}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              labelRowsPerPage="Projects per page"
            />
          </Paper>

          {orphanProposals.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                border: '1px solid rgba(183, 28, 28, 0.35)',
                overflow: 'hidden',
                mb: 3,
              }}
            >
              <SectionHeader
                title={`Proposals without a project (${orphanProposals.length})`}
                description="The project these were submitted to is no longer in the table, so they appear in no project row above."
              />
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <HeadCell>Proposal</HeadCell>
                      <HeadCell>Applicant</HeadCell>
                      <HeadCell>Project title on record</HeadCell>
                      <HeadCell>Outcome</HeadCell>
                      <HeadCell>Record</HeadCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orphanProposals.map((proposal) => (
                      <TableRow key={proposal.proposalID}>
                        <TableCell sx={{ minWidth: 220, fontWeight: 800 }}>
                          {proposal.proposalTitle || 'Untitled'}
                          <Typography sx={{ color: '#6b6b6b', fontWeight: 700, fontSize: '0.78rem' }}>
                            {proposal.proposalID}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 200 }}>
                          <Typography sx={{ fontWeight: 800 }}>{proposal.applicantName || '—'}</Typography>
                          <Typography sx={{ color: '#535353', fontWeight: 650, fontSize: '0.85rem' }}>
                            {proposal.applicantEmail || ''}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 220 }}>
                          {proposal.requestTitle || '—'}
                          <Typography sx={{ color: '#6b6b6b', fontWeight: 700, fontSize: '0.78rem' }}>
                            {proposal.projectID || 'no projectID'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={normalizeStatus(proposal.outcome) || '—'}
                            sx={{ fontWeight: 900, ...statusChipSx(proposal.outcome) }}
                          />
                        </TableCell>
                        <TableCell>
                          <PdfDownloadButton
                            label="Proposal PDF"
                            title="Download the full proposal record"
                            onDownload={() => downloadProposalPdf(proposal.proposalID)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: '1px solid rgba(0,0,0,0.08)',
              overflow: 'hidden',
            }}
          >
            <SectionHeader
              title="Email Log"
              description={
                emailTotals.total > emails.length
                  ? `Newest ${emails.length} of ${emailTotals.total} attempts the platform has made.`
                  : 'Every email the platform has attempted to send, newest first.'
              }
            />
            <TableContainer sx={{ overflowX: 'auto', maxHeight: 560 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <HeadCell>Sent at</HeadCell>
                    <HeadCell>Recipient</HeadCell>
                    <HeadCell>Type</HeadCell>
                    <HeadCell>Subject</HeadCell>
                    <HeadCell>Status</HeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {emails.map((entry, idx) => (
                    <TableRow
                      key={entry.emailID || idx}
                      sx={{
                        '&:nth-of-type(odd)': { backgroundColor: 'rgba(233, 228, 220, 0.35)' },
                      }}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>
                        {formatDateTime(entry.sentAt)}
                      </TableCell>
                      <TableCell sx={{ minWidth: 220, fontWeight: 700 }}>
                        {entry.recipient || '—'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {EMAIL_TYPE_LABELS[entry.emailType] || entry.emailType || '—'}
                        {entry.runKey ? (
                          <Typography sx={{ color: '#6b6b6b', fontWeight: 700, fontSize: '0.8rem' }}>
                            Run {entry.runKey}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell sx={{ minWidth: 300 }}>
                        {entry.subject ? <ClampedText text={entry.subject} lines={2} /> : '—'}
                        <ClampedText
                          text={entry.failureReason}
                          lines={2}
                          sx={{ color: '#b71c1c', fontWeight: 700, fontSize: '0.8rem', mt: 0.3 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={normalizeStatus(entry.status) || '—'}
                          sx={{ fontWeight: 900, ...statusChipSx(entry.status) }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  {emails.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography sx={{ color: '#707070', fontWeight: 800 }}>
                          {emailError || 'No emails have been sent yet.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default AdminProjectProposalSummary;
